import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchBlogData } from '../../features/BlogSlice/BlogSlice';
import { useTranslation } from 'react-i18next';
import Contact from '../ContactPage/Contact';
import Loading from '../Loading';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';

function BlogPage() {
    const { id } = useParams();
    const dispatch = useDispatch();

    const { t, i18n } = useTranslation();
    const { loading, error, blogs } = useSelector((state) => state.blog);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (id) {
            dispatch(fetchBlogData({ id }));
        }
    }, [id, dispatch, i18n.language]);

    // Close modal on ESC key press
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setIsModalOpen(false);
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);
    if(loading) return <Loading />;
    return (
        <div className="min-h-screen bg-gray-50">
            {loading && <Loading />}

            {/* Image Modal */}
            <AnimatePresence>
                {isModalOpen && blogs?.image && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                        onClick={() => setIsModalOpen(false)}
                    >
                        <button
                            className="absolute top-4 right-4 md:top-8 md:right-8 text-white hover:text-gray-300 z-10"
                            onClick={() => setIsModalOpen(false)}
                        >
                            <FaTimes size={28} />
                        </button>

                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            className="relative w-full  max-h-[90vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img

                                src={`${process.env.REACT_APP_IMG_URL}/${blogs.image}`}
                                alt={blogs.title}
                                className="rounded-lg shadow-xl object-contain max-h-[80vh] mx-auto"
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="max-w-7xl mx-auto px-4 py-8">
                {blogs && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                    >
                        {/* Hero Image */}
                        <motion.div
                            className=" rounded-md overflow-hidden mx-auto w-full flex justify-center md:w-10/12 xl:w-8/12"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.6, duration: 0.5 }}
                        >

                            <img
                                src={process.env.REACT_APP_IMG_URL + blogs?.image}
                                alt={blogs.title}
                                style={{ aspectRatio: '16/9' }}
                                className="  rounded-sm object-cover cursor-zoom-in hover:scale-105 transition-transform duration-500"
                                onClick={() => setIsModalOpen(true)}
                            />
                        </motion.div>

                        {/* Blog Content */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.6 }}
                            className="mt-8 space-y-6 w-full md:w-10/12 lg:w-10/12 xl:w-8/12 mx-auto bg-white rounded-xl shadow-sm p-6"
                        >
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="border-b border-gray-100 pb-6"
                            >
                                <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900">
                                    {blogs.title}
                                </h1>
                                <p className="text-gray-600 text-sm font-medium">
                                    {new Date(blogs.created_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.7 }}
                                dangerouslySetInnerHTML={{ __html: blogs.description }}
                                className="prose prose-sm sm:prose-base max-w-none text-gray-700
                                        prose-headings:text-gray-900 prose-a:text-blue-600 
                                        prose-img:rounded-lg prose-img:shadow-sm prose-img:mx-auto
                                        prose-blockquote:border-l-4 prose-blockquote:border-blue-500
                                        prose-blockquote:bg-gray-50 prose-blockquote:px-4 prose-blockquote:py-2"
                            />
                        </motion.div>
                    </motion.div>
                )}
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className='mt-8 w-full'
            >
                <Contact />
            </motion.div>
        </div>
    )
}

export default BlogPage;